// src/hooks/useProjects.js
import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * Hook to get the current user's project (for clients)
 * Automatically fetches based on authenticated user's email
 */
export function useClientProject() {
  return useQuery({
    queryKey: ["client-project"],
    queryFn: async () => {
      const getClientProject = httpsCallable(functions, "getClientProject");
      const result = await getClientProject();
      return result.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get a specific project by ID
 * @param {string} projectId - The project ID to fetch
 */
export function useProject(projectId) {
  return useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      const getProject = httpsCallable(functions, "getProject");
      const result = await getProject({ projectId });
      return result.data;
    },
    enabled: !!projectId, // Only run if projectId exists
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get all projects (for admin portal)
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (optional)
 * @param {number} options.limit - Max number of records (optional)
 */
export function useAllProjects(options = {}) {
  return useQuery({
    queryKey: ["all-projects", options],
    queryFn: async () => {
      const getAllProjects = httpsCallable(functions, "getAllProjects");
      const result = await getAllProjects(options);
      return result.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to get project statistics
 * @param {string} projectId - The project ID
 */
export function useProjectStats(projectId) {
  return useQuery({
    queryKey: ["project-stats", projectId],
    queryFn: async () => {
      const getProjectStats = httpsCallable(functions, "getProjectStats");
      const result = await getProjectStats({ projectId });
      return result.data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch active projects (Pre-Construction & Active Construction)
 * Useful for dropdowns when creating tasks, change orders, etc.
 *
 * @param {Object} options - Optional filter options
 * @param {boolean} options.showTestAccounts - Include test accounts (IT only)
 * @returns {Object} Query result with projects array
 */
export function useActiveProjects({ showTestAccounts = false } = {}) {
  return useQuery({
    queryKey: ["projects", "active", { showTestAccounts }],
    queryFn: async () => {
      const getActiveProjects = httpsCallable(functions, "getActiveProjects");
      const result = await getActiveProjects({ showTestAccounts });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch active projects");
      }

      return result.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get plan documents for a project
 * Uses getProjectDocuments which returns organized document categories
 * @param {string} projectId - The project ID
 */
export function usePlanDocuments(projectId) {
  return useQuery({
    queryKey: ["plan-documents", projectId],
    queryFn: async () => {
      const getProjectDocuments = httpsCallable(
        functions,
        "getProjectDocuments"
      );
      const result = await getProjectDocuments({ projectId });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch documents");
      }

      // Helper to normalize attachment objects
      const normalizeDoc = (doc) => ({
        id: doc.id,
        name: doc.filename || doc.name || "Untitled",
        url: doc.url,
        thumbnail:
          doc.thumbnails?.large?.url ||
          doc.thumbnails?.small?.url ||
          doc.thumbnail ||
          null,
        type: doc.type?.includes("pdf") ? "pdf" : "image",
        size: doc.size || null,
      });

      // Map to expected structure with normalized documents
      const docs = result.data.data || {};
      return {
        floorplans: (docs.floorPlans || []).map(normalizeDoc),
        siteplans: (docs.sitePlans || []).map(normalizeDoc),
        other: (docs.otherDocuments || docs.other || []).map(normalizeDoc),
      };
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
  });
}
